import React, { useState, useMemo } from 'react';
import { Badge } from '../../../ui/badge';
import { Checkbox } from '../../../ui/checkbox';
import { Label } from '../../../ui/label';
import { useDatasets } from '../../../../hooks/useDatasets';
import IconWrapper from '../../../IconWrapper';
import { FiChevronDown, FiX } from 'react-icons/fi';

export interface DatasetFilterProps {
  selectedDatasetIds: string[];
  onDatasetChange: (datasetIds: string[]) => void;
  allowedDatasetIds?: string[]; // When set, only these datasets are available
}

const DatasetFilter: React.FC<DatasetFilterProps> = ({
  selectedDatasetIds,
  onDatasetChange,
  allowedDatasetIds,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch real datasets from API
  const { data: datasetsResponse } = useDatasets();
  const allDatasets = datasetsResponse?.datasets || [];

  // Filter datasets based on allowed IDs (from active view)
  const datasets = allowedDatasetIds && allowedDatasetIds.length > 0
    ? allDatasets.filter(d => allowedDatasetIds.includes(d.id))
    : allDatasets;

  // Filter datasets by search query
  const filteredDatasets = useMemo(() => {
    if (!searchQuery.trim()) return datasets;
    const query = searchQuery.toLowerCase();
    return datasets.filter(d => d.name.toLowerCase().includes(query));
  }, [datasets, searchQuery]);

  // Get selected datasets for display
  const selectedDatasets = useMemo(() => {
    return datasets.filter(d => selectedDatasetIds.includes(d.id));
  }, [datasets, selectedDatasetIds]);

  const toggleDataset = (datasetId: string) => {
    const newSelection = selectedDatasetIds.includes(datasetId)
      ? selectedDatasetIds.filter(id => id !== datasetId)
      : [...selectedDatasetIds, datasetId];
    onDatasetChange(newSelection);
  };

  const removeDataset = (datasetId: string) => {
    onDatasetChange(selectedDatasetIds.filter(id => id !== datasetId));
  };


  const getTypeBadge = (type: string) => {
    return (
      <Badge variant="outline" className="text-xs">
        {type}
      </Badge>
    );
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
        Collection:
      </span>

      {/* Dropdown Trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="min-w-[200px] max-w-[350px] h-[35px] px-3 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 flex items-center justify-between"
        >
          <span className="truncate">All Collections</span>
          <IconWrapper icon={FiChevronDown} className="h-4 w-4 text-gray-500 flex-shrink-0 ml-2" />
        </button>

        {/* Dropdown Content */}
        {dropdownOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => {
                setDropdownOpen(false);
                setSearchQuery('');
              }}
            />

            {/* Dropdown Menu */}
            <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-md shadow-lg w-[350px]">
              {/* Search */}
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  placeholder="Search Collections..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-go-primary focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Dataset List */}
              <div className="max-h-60 overflow-y-auto p-2">
                {filteredDatasets.length > 0 ? (
                  <div className="space-y-1">
                    {filteredDatasets.map((dataset) => (
                      <div
                        key={dataset.id}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={() => toggleDataset(dataset.id)}
                      >
                        <Checkbox
                          id={`dataset-${dataset.id}`}
                          checked={selectedDatasetIds.includes(dataset.id)}
                          onCheckedChange={() => toggleDataset(dataset.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Label
                          htmlFor={`dataset-${dataset.id}`}
                          className="text-sm font-normal cursor-pointer flex items-center gap-2 flex-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="flex-1 truncate">{dataset.name}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {getTypeBadge(dataset.type)}
                            <Badge variant="outline" className="text-xs">
                              {dataset.recordCount.toLocaleString()}
                            </Badge>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    No Collections match your search
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {selectedDatasetIds.length > 0 && (
        <Badge
          variant="outline"
          className="text-xs bg-[#EEF2FF] border-[#DDE2FF] text-[#4342FF]"
        >
          Filtered by Collection
        </Badge>
      )}
    </div>
  );
};

export default DatasetFilter;